import React, { useState, useEffect } from 'react';
import {
  Dialog,
  ScrollableDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/scrollable-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RoleBadge } from './RoleBadge';
import { InviteMemberData } from '@/lib/api/services/teamService';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { Eye, EyeOff, Copy, Check, Building2, Phone, CheckCircle, AlertCircle } from 'lucide-react';

interface MinimalInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (userData: InviteMemberData) => void;
}

export const MinimalInviteDialog: React.FC<MinimalInviteDialogProps> = ({
  open,
  onOpenChange,
  onInvite
}) => {
  const { user: currentUser } = useAuth();
  const roleRaw = (currentUser?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(currentUser as any)?.is_super_admin;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin;
  const isRegularUser = !isAdmin && roleRaw === 'USER';
  const defaultRole: InviteMemberData['role'] = isRegularUser ? 'AGENT' : 'USER';
  const [formData, setFormData] = useState<InviteMemberData>({
    email: '',
    name: '',
    role: defaultRole,
    password: '',
    hasAllConversationsAccess: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedBusinessAccounts, setSelectedBusinessAccounts] = useState<string[]>([]);
  const [accountPermissions, setAccountPermissions] = useState<Record<string, { has_all_conversations_access: boolean }>>({});

  const { data: businessAccounts, isLoading: isLoadingBusinessAccounts } = useBusinessAccounts();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFormData({ email: '', name: '', role: defaultRole, password: '', hasAllConversationsAccess: false });
      setShowPassword(false);
      setPasswordError('');
      setIsGenerated(false);
      setCopied(false);
      // Auto-select the only account if there's just one
      if (businessAccounts && businessAccounts.length === 1) {
        setSelectedBusinessAccounts([businessAccounts[0].id]);
      } else {
        setSelectedBusinessAccounts([]);
      }
      setAccountPermissions({});
    }
  }, [open, defaultRole]);

  // Auto-select all business accounts for ADMIN role
  useEffect(() => {
    if (formData.role === 'ADMIN' && businessAccounts) {
      const allIds = businessAccounts.map(ba => ba.id);
      setSelectedBusinessAccounts(allIds);
      // For Admins, enable "All Conversations" by default for all accounts
      const perms: Record<string, { has_all_conversations_access: boolean }> = {};
      allIds.forEach(id => {
        perms[id] = { has_all_conversations_access: true };
      });
      setAccountPermissions(perms);
    } else if (open && formData.role !== 'ADMIN' && businessAccounts && selectedBusinessAccounts.length === 0) {
      // For non-admins, maybe select the first one by default?
      // Keeping it empty to force conscious selection, or maybe select all but without full permissions?
      // Let's stick to empty initiate state if not admin, or select the first one for convenience.
      if (businessAccounts.length > 0) {
        setSelectedBusinessAccounts([businessAccounts[0].id]);
      }
    }
  }, [formData.role, businessAccounts, open]);

  const isSingleAccount = !!(businessAccounts && businessAccounts.length === 1);

  const handleBusinessAccountToggle = (businessAccountId: string) => {
    // Prevent deselecting when it's the only business account
    if (isSingleAccount) return;
    setSelectedBusinessAccounts(prev =>
      prev.includes(businessAccountId)
        ? prev.filter(id => id !== businessAccountId)
        : [...prev, businessAccountId]
    );
  };

  const handleValidationToggle = (businessAccountId: string, checked: boolean) => {
    setAccountPermissions(prev => ({
      ...prev,
      [businessAccountId]: { ...prev[businessAccountId], has_all_conversations_access: checked }
    }));
  };

  const handleSelectAll = () => {
    if (businessAccounts) {
      setSelectedBusinessAccounts(businessAccounts.map(ba => ba.id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedBusinessAccounts([]);
  };

  const validatePassword = (password: string): string => {
    if (!password) {
      return ''; // Password is optional
    }
    const hasMinLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);

    if (!hasMinLength || !hasUpper || !hasLower || !hasNumber || !hasSpecial) {
      return 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';
    }
    return '';
  };

  const generateRandomPassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const special = "!@#$%^&*";
    const all = upper + lower + numbers + special;
    // Guarantee at least one character from each required category
    const chars = [
      upper.charAt(Math.floor(Math.random() * upper.length)),
      lower.charAt(Math.floor(Math.random() * lower.length)),
      numbers.charAt(Math.floor(Math.random() * numbers.length)),
      special.charAt(Math.floor(Math.random() * special.length)),
    ];
    for (let i = 4; i < 12; i++) {
      chars.push(all.charAt(Math.floor(Math.random() * all.length)));
    }
    // Shuffle so the guaranteed chars aren't always at the start
    const password = chars.sort(() => Math.random() - 0.5).join('');
    setFormData(prev => ({ ...prev, password }));
    setPasswordError('');
    setIsGenerated(true);
    setCopied(false);
  };

  const handlePasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, password: value }));
    const error = validatePassword(value);
    setPasswordError(error);
    setIsGenerated(false);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (formData.password) {
      await navigator.clipboard.writeText(formData.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password if provided
    if (formData.password) {
      const error = validatePassword(formData.password);
      if (error) {
        setPasswordError(error);
        return;
      }
    }

    // Construct assignments
    const assignments = selectedBusinessAccounts.map(accountId => ({
      business_account_id: accountId,
      permissions: {
        // For ADMIN/USER, force full access. For AGENT, use selection (default false)
        has_all_conversations_access: formData.role === 'AGENT'
          ? (accountPermissions[accountId]?.has_all_conversations_access || false)
          : true
      }
    }));

    const inviteData: InviteMemberData = {
      email: formData.email,
      name: formData.name,
      role: formData.role,
      assignments,
      // Keep legacy for backward compatibility if needed, using IDs
      businessAccountIds: selectedBusinessAccounts,
      hasAllConversationsAccess: false
    };

    // Include password only if it's provided and not empty
    if (formData.password && typeof formData.password === 'string' && formData.password.trim().length > 0) {
      inviteData.password = formData.password.trim();
    }

    onInvite(inviteData);
  };

  const dialogHeader = (
    <DialogHeader>
      <DialogTitle className="text-[20px] md:text-[22px] text-left font-semibold">Add Team Member</DialogTitle>
      <DialogDescription className="text-left">
        Add a new member to your team
      </DialogDescription>
    </DialogHeader>
  );

  const dialogFooter = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="invite-member-form"
        disabled={!formData.email || !formData.name || !formData.password || !!passwordError}
      >
        Add Member
      </Button>
    </DialogFooter>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ScrollableDialogContent
        className=" max-h-[95vh]"
        maxWidthXs="98vw"
        maxWidthSm="90vw"
        maxWidthMd="70vw"
        maxWidthLg="70vw"
        maxWidthXl="50vw"
        maxWidthXxl="30vw"
        header={dialogHeader}
        footer={dialogFooter}
      >
        <form id="invite-member-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => {
                  if (isRegularUser) {
                    setFormData(prev => ({ ...prev, role: 'AGENT' }));
                    return;
                  }
                  setFormData(prev => ({ ...prev, role: value as 'ADMIN' | 'AGENT' | 'USER' }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {!isRegularUser && (
                    <SelectItem value="USER">
                      <div className="flex items-center gap-2">
                        <RoleBadge role="USER" size="sm" showIcon={false} />
                        <span>User</span>
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="AGENT">
                    <div className="flex items-center gap-2">
                      <RoleBadge role="AGENT" size="sm" showIcon={false} />
                      <span>Agent</span>
                    </div>
                  </SelectItem>
                  {isAdmin && (
                    <SelectItem value="ADMIN">
                      <div className="flex items-center gap-2">
                        <RoleBadge role="ADMIN" size="sm" showIcon={false} />
                        <span>Admin</span>
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateRandomPassword}
                  className="text-xs"
                >
                  Generate Random
                </Button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Leave empty to auto-generate random password"
                  value={formData.password || ''}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={passwordError ? "border-red-500" : ""}
                />
                <div className="absolute right-0 top-0 h-full flex items-center">
                  {isGenerated && formData.password && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="px-2 py-2 hover:bg-transparent"
                      onClick={handleCopyPassword}
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {passwordError && (
                <p className="text-xs text-red-600">{passwordError}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Business Accounts Access</Label>
              {!isSingleAccount && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={isLoadingBusinessAccounts}
                  >
                    Deselect All
                  </Button>
                </div>
              )}
            </div>

            <div className="text-sm text-muted-foreground">
              Choose which business accounts this team member can access and configure permissions.
            </div>

            {isLoadingBusinessAccounts ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading business accounts...</span>
              </div>
            ) : businessAccounts && businessAccounts.length > 0 ? (
              <Card>
                <CardContent className="p-4 space-y-3">
                  {businessAccounts.map((businessAccount) => {
                    const isSelected = selectedBusinessAccounts.includes(businessAccount.id);
                    const hasAllAccess = accountPermissions[businessAccount.id]?.has_all_conversations_access || false;

                    return (
                      <div
                        key={businessAccount.id}
                        className={`flex flex-col p-3 rounded-lg border transition-colors ${isSelected
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                          }`}
                      >
                        {/* Account Selection Row */}
                        <div
                          className={`flex items-center justify-between w-full ${isSingleAccount ? 'cursor-default' : 'cursor-pointer'}`}
                          onClick={() => handleBusinessAccountToggle(businessAccount.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="font-medium">{businessAccount.name}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {businessAccount.phone_number}
                                </div>
                              </div>
                            </div>
                            <Badge
                              variant={businessAccount.is_active ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {businessAccount.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>

                          <div className="flex items-center">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                        </div>

                        {/* Permissions Row (Visible only if selected and role is AGENT) */}
                        {isSelected && formData.role === 'AGENT' && (
                          <div className="mt-3 pl-9 border-t pt-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`perm-all-${businessAccount.id}`}
                                checked={hasAllAccess}
                                onCheckedChange={(checked) => handleValidationToggle(businessAccount.id, !!checked)}
                              />
                              <Label
                                htmlFor={`perm-all-${businessAccount.id}`}
                                className="text-sm cursor-pointer"
                              >
                                View All Conversations
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              Can see and manage all conversations for this business account.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No business accounts found. Create a business account first.
                  </p>
                </CardContent>
              </Card>
            )}

            {selectedBusinessAccounts.length > 0 && (
              <div className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {selectedBusinessAccounts.length} business account{selectedBusinessAccounts.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </form>
      </ScrollableDialogContent>
    </Dialog>
  );
};
